import { apiClient } from './apiClient';
import type {
  AuthResponse,
  SignInRequest,
  SignUpRequest,
  SignUpResponse,
  ForgotPasswordRequest,
  ForgotPasswordResponse,
  ResetPasswordRequest,
  ResetPasswordResponse,
  AccountDeletionRequest,
  AccountDeletionResponse
} from '../types/auth';

// Same web client ID used by mobile (spotto-f5395 GCP project)
const GOOGLE_CLIENT_ID = '771671722154-fl6iq7gb1hjrfdu86vfpddigurr02t5d.apps.googleusercontent.com';

// Apple Services ID for web Sign in with Apple
const APPLE_SERVICES_ID = 'pl.spotto.web';
const APPLE_REDIRECT_URI = `${window.location.origin}/auth/apple/callback`;

export const authService = {
  async signIn(credentials: SignInRequest): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/Auth/signin', credentials);
  },

  async signInWithGoogle(): Promise<AuthResponse> {
    return new Promise((resolve, reject) => {
      const google = (window as any).google;
      if (!google?.accounts?.id) {
        reject(new Error('Google Identity Services not loaded'));
        return;
      }

      google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: async (response: { credential: string }) => {
          try {
            const authResponse = await apiClient.post<AuthResponse>(
              '/Auth/external/google',
              { idToken: response.credential }
            );
            resolve(authResponse);
          } catch (error) {
            reject(error);
          }
        },
        cancel_on_tap_outside: true,
      });

      google.accounts.id.prompt((notification: any) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // One Tap not available, fall back to popup button
          const popupDiv = document.createElement('div');
          popupDiv.id = 'google-signin-popup';
          popupDiv.style.position = 'fixed';
          popupDiv.style.top = '0';
          popupDiv.style.left = '0';
          popupDiv.style.width = '100vw';
          popupDiv.style.height = '100vh';
          popupDiv.style.display = 'flex';
          popupDiv.style.alignItems = 'center';
          popupDiv.style.justifyContent = 'center';
          popupDiv.style.backgroundColor = 'rgba(0,0,0,0.5)';
          popupDiv.style.zIndex = '9999';

          popupDiv.addEventListener('click', (e) => {
            if (e.target === popupDiv) {
              popupDiv.remove();
              reject(new Error('Sign-in cancelled'));
            }
          });

          const buttonContainer = document.createElement('div');
          buttonContainer.style.backgroundColor = '#1c1c1e';
          buttonContainer.style.padding = '32px';
          buttonContainer.style.borderRadius = '16px';
          buttonContainer.style.border = '1px solid #333';

          popupDiv.appendChild(buttonContainer);
          document.body.appendChild(popupDiv);

          google.accounts.id.renderButton(buttonContainer, {
            type: 'standard',
            theme: 'filled_black',
            size: 'large',
            text: 'signin_with',
            shape: 'rectangular',
            width: 300,
          });
        }
      });
    });
  },

  async signInWithApple(): Promise<AuthResponse> {
    const AppleID = (window as any).AppleID;
    if (!AppleID?.auth) {
      throw new Error('Apple Sign-In not loaded');
    }

    AppleID.auth.init({
      clientId: APPLE_SERVICES_ID,
      scope: 'name email',
      redirectURI: APPLE_REDIRECT_URI,
      usePopup: true,
    });

    try {
      const response = await AppleID.auth.signIn();
      const idToken = response?.authorization?.id_token;
      if (!idToken) {
        throw new Error('No identity token received from Apple');
      }

      // Apple only returns name/email on first sign-in
      const user = response?.user;
      const fullName = user?.name
        ? { firstName: user.name.firstName, lastName: user.name.lastName }
        : null;
      const email = user?.email ?? null;

      return apiClient.post<AuthResponse>('/Auth/external/apple', {
        idToken,
        fullName,
        email,
      });
    } catch (err: any) {
      if (err?.error === 'popup_closed_by_user' || err?.error === 'user_cancelled_authorize') {
        throw new Error('Sign-in cancelled');
      }
      throw err;
    }
  },

  async signUp(data: SignUpRequest): Promise<SignUpResponse> {
    return apiClient.post<SignUpResponse>('/Auth/signup', data);
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    return apiClient.post<AuthResponse>('/Auth/refresh', { refreshToken });
  },

  async forgotPassword(request: ForgotPasswordRequest): Promise<ForgotPasswordResponse> {
    return apiClient.post<ForgotPasswordResponse>('/auth/forgot-password', request);
  },

  async resetPassword(request: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    return apiClient.post<ResetPasswordResponse>('/auth/reset-password', request);
  },

  async requestAccountDeletion(request: AccountDeletionRequest): Promise<AccountDeletionResponse> {
    return apiClient.post<AccountDeletionResponse>('/auth/delete-account', request);
  },

  async verifyEmail(token: string): Promise<{ message: string }> {
    return apiClient.get<{ message: string }>(`/auth/verify-email?token=${encodeURIComponent(token)}`);
  },

  // Storage helpers
  storage: {
    getToken(): string | null {
      return localStorage.getItem('auth_token');
    },

    setToken(token: string): void {
      localStorage.setItem('auth_token', token);
    },

    getRefreshToken(): string | null {
      return localStorage.getItem('refresh_token');
    },

    setRefreshToken(token: string): void {
      localStorage.setItem('refresh_token', token);
    },

    getUser(): string | null {
      return localStorage.getItem('user');
    },

    setUser(user: string): void {
      localStorage.setItem('user', user);
    },

    clear(): void {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('refresh_token');
      localStorage.removeItem('user');
    },
  },
};
